import InnerScreenHeader from "@/components/layout/inner-screen-header"
import { useUser } from "@/api/users"
import Loader from "@/components/layout/loader"
import {
  FormCard,
  FormCardBlock,
  FormCardLeftColumn,
  FormCardRightColumn,
} from "@/components/ux/form.card"
import { Button } from "@/components/ui/button"
import { WalletEjectModal } from "@/wallet/wallet-eject.modal"
import { WalletBackupModal } from "@/wallet/wallet-backup.modal"
import { BackupMethods } from "@portal-hq/web"
import { WalletSetPasswordModal } from "@/wallet/wallet-set-password.modal"
import { WalletBackup, useWallets } from "@/api/wallets"
import CheckIcon from "@/components/icons/check"
import Separator from "@/components/ux/separator"

const LastBackup = ({ backup }: { backup: WalletBackup }) => {
  return (
    <div className="text-xs text-gray-300 flex flex-row gap-2 items-center">
      <CheckIcon />
      Last backup on {new Date(backup.updatedAt!).toLocaleDateString()}
    </div>
  )
}

export default function WalletScreen() {
  const { user } = useUser()
  const { wallet } = useWallets()

  if (!user || !wallet) {
    return <Loader message="Loading..." />
  }

  const gdriveBackup = wallet.walletBackups.find((backup: WalletBackup) =>
    backup.method.startsWith(BackupMethods.gdrive),
  )
  const passkeyBackup = wallet.walletBackups.find((backup: WalletBackup) =>
    backup.method.startsWith(BackupMethods.passkey),
  )

  return (
    <div className="flex flex-col gap-4">
      <InnerScreenHeader title="Settings" to="/settings" />
      <FormCard title="Wallet">
        <FormCardBlock>
          <div className="flex flex-col md:flex-row gap-6">
            <FormCardLeftColumn>
              Change your wallet secret PIN
            </FormCardLeftColumn>
            <FormCardRightColumn>
              <WalletSetPasswordModal>
                <Button
                  variant="outline"
                  disabled={!user.hasWallet}
                  data-testid="wallet-set-password"
                >
                  Change Secret PIN
                </Button>
              </WalletSetPasswordModal>
            </FormCardRightColumn>
          </div>
        </FormCardBlock>
        <FormCardBlock>
          <div className="flex flex-col md:flex-row gap-6">
            <FormCardLeftColumn>
              BraidPay is a self-custodial service. You can export your private
              key at any time.
            </FormCardLeftColumn>
            <FormCardRightColumn>
              <WalletEjectModal>
                <Button
                  variant="outline"
                  disabled={!user.hasWallet}
                  data-testid="export-private-key"
                >
                  Export Private Key
                </Button>
              </WalletEjectModal>
            </FormCardRightColumn>
          </div>
        </FormCardBlock>
        <FormCardBlock>
          <div className="flex flex-col md:flex-row gap-6">
            <FormCardLeftColumn>Backup your wallet</FormCardLeftColumn>
            <FormCardRightColumn>
              <div className="flex flex-col gap-4">
                <WalletBackupModal method={BackupMethods.gdrive}>
                  <Button variant="outline" disabled={!user.hasWallet}>
                    Backup Wallet with Google Drive
                  </Button>
                </WalletBackupModal>
                {gdriveBackup && <LastBackup backup={gdriveBackup} />}
                <Separator />
                <WalletBackupModal method={BackupMethods.passkey}>
                  <Button variant="outline" disabled={!user.hasWallet}>
                    Backup Wallet with Passkey
                  </Button>
                </WalletBackupModal>
                {passkeyBackup && <LastBackup backup={passkeyBackup} />}
              </div>
            </FormCardRightColumn>
          </div>
        </FormCardBlock>
      </FormCard>
    </div>
  )
}
